import React, { useRef, useEffect, useState } from "react";
import * as d3 from "d3";

const BarChart = ({ barData, totalValue, blackKey = "A", grayKey = "B" }) => {
  const svgRef = useRef();
  const containerRef = useRef();

  const [selectedWaterType, setSelectedWaterType] = useState("both");
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const handleToggle = (waterType) => {
    setSelectedWaterType((prevType) =>
      prevType === waterType ? "both" : waterType
    );
  };
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        const height = Math.max(400, Math.min(600, width / 2));
        setDimensions({ width, height });
      }
    };

    handleResize();

    const resizeObserver = new ResizeObserver(handleResize);
    if (containerRef.current) resizeObserver.observe(containerRef.current);

    return () => resizeObserver.disconnect();
  }, []);


  
  useEffect(() => {
    if (!barData || barData.length === 0) {
      return;
    }

    const margin = { top: 40, right: 10, bottom: 60, left: 40 };
    const width = dimensions.width -margin.right -margin.left;
    const height = dimensions.height - margin.bottom ;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    // Set svg attributes for responsiveness
    svg
      .attr("viewBox", `0 0 ${dimensions.width} ${dimensions.height+40}`)
      .attr("preserveAspectRatio", "xMidYMid meet")
      .style("width", "100%")
      .style("height", "auto")
      .style("font-family", "sans-serif");

    svg.selectAll("*").remove();

    const x0 = d3
      .scaleBand()
      .range([0, width])
      .padding(0.1)
      .domain(barData.map((d) => d.category));

    const x1 = d3
      .scaleBand()
      .padding(0.05)
      .domain([blackKey, grayKey])
      .rangeRound([0, x0.bandwidth()]);

    const maxValue = d3.max(barData, (d) => Math.max(d[blackKey], d[grayKey]));
    const maxPercent = Math.ceil((maxValue / totalValue) * 100);
    const axisFontSize = Math.max(10, Math.min(14, width / 40));

    const y = d3
      .scaleLinear()
      .range([height, 0])
      .domain([0, maxPercent])
      .nice();

      const legend = svg
      .append("g")
      .attr("class", "legend")
      .attr("transform", `translate(${dimensions.width / 2 - 100})`); 

    // Black Water
    const blackLegend = legend.append("g")
      .style("cursor", "pointer")
      .on("click", () => handleToggle("black"));

    blackLegend
      .append("rect")
      .attr("x", 0)
      .attr("y", 0)
      .attr("width", 30)
      .attr("height", 16)
      .attr("fill", "black")
      

    blackLegend
      .append("text")
      .attr("x", 35)
      .attr("y", 12) 
      .text("Black Water")
      .style("font-size", "14px")
      .style("fill", "#000")
      .style("text-decoration", selectedWaterType === "black" ? "line-through" : "none"); 

    // Gray Water
    const grayLegend = legend.append("g")
      .style("cursor", "pointer")
      .on("click", () => handleToggle("gray"));

    grayLegend
      .append("rect")
      .attr("x", 130)
      .attr("y", 0)
      .attr("width", 30)
      .attr("height", 16)
      .attr("fill", "#808080")
     

    grayLegend
      .append("text")
      .attr("x", 165)
      .attr("y", 12) 
      .text("Gray Water")
      .style("font-size", "14px")
      .style("fill", "#000")
      .style("text-decoration", selectedWaterType === "gray" ? "line-through" : "none"); // Line through jodi toggle off

    // Grid
    svg
      .append("g")
      .attr("class", "grid y-grid")
      .attr("transform", `translate(${margin.left}, ${margin.top})`)
      .call(d3.axisLeft(y).tickSize(-width).tickFormat(""))
      .call((g) => g.selectAll(".domain").remove())
      .call((g) => g.selectAll("line").attr("stroke", "#e0e0e0"));

    svg
      .append("g")
      .attr("class", "grid x-grid")
      .attr("transform", `translate(${margin.left}, ${height + margin.top})`)
      .call(d3.axisBottom(x0).tickSize(-height).tickFormat(""))
      .call((g) => g.selectAll(".domain").remove())
      .call((g) =>
        g
          .selectAll("line")
          .attr("stroke", "#e0e0e0")
          .attr("shape-rendering", "crispEdges")
      );

    svg
      .append("g")
      .attr("class", "grid y-grid-right")
      .attr("transform", `translate(${width + margin.left}, ${margin.top})`)
      .call(d3.axisRight(y).tickSize(0).tickFormat(""))
      .call((g) =>
        g
          .selectAll(".domain")
          .attr("stroke", "#e0e0e0")
          .attr("shape-rendering", "crispEdges")
      )
      .call((g) => g.selectAll(".tick").remove());

    // X Axis
    svg
      .append("g")
      .attr("class", "x-axis")
      .attr("transform", `translate(${margin.left},${height + margin.top})`)
      .call(d3.axisBottom(x0))
      .selectAll("text")
      .attr("fill", "gray")
      .style("font-size", `${axisFontSize}px`)
      .style("text-anchor", width < 700 ? "end" : "middle")
      .attr("transform", width < 700 ? "rotate(-15)" : "rotate(0)");

    svg.select(".x-axis path").attr("stroke", "#e0e0e0");
    svg.select(".x-axis").selectAll(".tick line").attr("stroke", "#e0e0e0");

    // Y Axis
    svg
      .append("g")
      .attr("class", "y-axis")
      .attr("transform", `translate(${margin.left},${margin.top})`)
      .call(
        d3
          .axisLeft(y)
          .ticks(Math.min(10, Math.ceil(height / 45)))
          .tickFormat((d) => `${d}%`)
      )
      .selectAll("text")
      .attr("fill", "gray")
      .style("font-size", `${axisFontSize}px`);

    svg.select(".y-axis path").attr("stroke", "#e0e0e0");
    svg.select(".y-axis").selectAll(".tick line").attr("stroke", "#e0e0e0");

    // Bars container
    const barsContainer = svg
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    const tooltip = d3
      .select("body")
      .append("div")
      .attr("class", "tooltip")
      .style("position", "absolute")
      .style("background-color", "#000")
      .style("color", "#fff")
      .style("font-size",`${axisFontSize}px`)
      .style("border", "1px solid #ddd")
      .style("padding", "5px")
      .style("border-radius", "5px")
      .style("opacity", 0.7)
      .style("box-shadow", "0 2px 10px rgba(0, 0, 0, 0.1)");

    barsContainer
      .selectAll("g.bar-group")
      .data(barData)
      .enter()
      .append("g")
      .attr("class", "bar-group")
      .attr("transform", (d) => `translate(${x0(d.category)},0)`)
      .selectAll("rect")
      .data((d) => {
        if (selectedWaterType === "both") {
          return [
            { key: blackKey, value: d[blackKey], color: "#000000" },
            { key: grayKey, value: d[grayKey], color: "#808080" },
          ];
        } else if (selectedWaterType === "black") {
          return [{ key: blackKey, value: d[blackKey], color: "#000000" }];
        } else if (selectedWaterType === "gray") {
          return [{ key: grayKey, value: d[grayKey], color: "#808080" }];
        }
      })
      .enter()
      .append("rect")
      .attr("x", (d) => (selectedWaterType === "both" ? x1(d.key) : 0))
      .attr("width", (d) =>
        selectedWaterType === "both"
          ? x1.bandwidth() * 0.8
          : x0.bandwidth() * 0.8
      )
      .attr("y", height)
      .attr("height", 0)
      .attr("fill", (d) => d.color)
      .on("mouseover", function (event, d) {
        tooltip.style("opacity", "100%").html(`
          ${d3.select(this.parentNode).datum().category}<br>
          <span style="display: inline-block; width: 10px; height: 10px;
                       background-color: ${d.color};
                       border: 1px solid #ffffff;
                       margin-right: 5px;"></span>
          ${
            d.key === blackKey ? "Black" : "Gray"
          } Water: ${Math.floor((d.value / totalValue) * 100)}%
        `);
      })
      .on("mousemove", function (event) {
        tooltip
          .style("top", event.pageY + 5 + "px")
          .style("left", event.pageX + 5 + "px");
      })
      .on("mouseout", function () {
        tooltip.style("opacity", "0");
      })
      .transition()
      .duration(600)
      .ease(d3.easeLinear)
      .attr("x", (d) => (selectedWaterType === "both" ? x1(d.key) : 0))
      .attr("y", (d) => y(Math.floor((d.value / totalValue) * 100)))
      .attr(
        "height",
        (d) => Math.abs(height - y(Math.floor((d.value / totalValue) * 100)))
      );
  }, [barData, dimensions, totalValue, selectedWaterType, blackKey, grayKey]);

  return (
 
      <div ref={containerRef}>
        <svg ref={svgRef}></svg>
      </div>

  );
};

export default BarChart;
